import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

// Components
import { Flex, useBreakpointValue } from '@wegroup/design-system';
import BackButton from '../../../common/src/components/BackButton';
import AssistantQuestion from '../components/flow/AssistantQuestion';

// Services
import { markFlowAsComplete } from '../services/louiseApiService';
import useSessionId from '../../../flows/src/hooks/useSessionId';
import ViewHeader from '../components/legacy/question-view-header/QuestionViewHeader';
import LoadingSpinner from '../../../common/src/components/LoadingSpinner';
import BrokerCardContainer from '../../../flows/src/components/ui/common/components/finalization/BrokerCardContainer';
import useTracking from '../../../common/src/hooks/tracking/useTracking';
import useAuth from '../../../app/src/features/authentication/hooks/useAuth';
import { isSocialLoginUrl } from '../../../app/src/features/authentication/constants/SocialAuth';
import { useCreateRecommendationByParty } from '../../../parties/src/features/detail/api/postRecommendationsByParty';
import { useConversation } from '../../../conversations/src/features/detail/shared/api/getConversationById';

const CampaignsFinalizationView: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const history = useHistory();
  const sessionId = useSessionId();
  const { t } = useTranslation();
  const { trackEvent } = useTracking();
  const [loading, setLoading] = useState<boolean>(true);
  const isMobile = useBreakpointValue({ base: true, laptop: false });
  const { mutateAsync: createRecommendation } =
    useCreateRecommendationByParty();
  const { data: conversation } = useConversation(sessionId);

  const { distribution } = useAuth();

  useEffect(() => {
    if (conversation?.prospect?.id) {
      initial();
    }
  }, [conversation]);

  const initial = async () => {
    let recommendationResponse;
    if (!isSocialLoginUrl && conversation?.prospect?.id) {
      recommendationResponse = await createRecommendation(
        conversation.prospect.id,
      );
    }
    await markFlowAsComplete({
      conversationId: sessionId,
      recommendationId: recommendationResponse?.id,
    });
    trackEvent('Finalize Sales Conversation');
    setLoading(false);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <>
      {isMobile && <ViewHeader />}
      <Flex
        width="100%"
        height="100%"
        flexDir="column"
        background="white"
        overflowY="scroll"
        p={{ base: '3', tablet: '6' }}
        pb={{ base: '16', laptopL: '6' }}
      >
        <Flex maxW="4xl" flexDir="column" margin="0 auto">
          <StyledAssistantQuestion
            question={
              isSocialLoginUrl
                ? t('FLOWS_CAMPAIGNS_FINALIZATION_TITLE', {
                    distribution: distribution.name,
                  })
                : t(
                    'Everything was saved correctly You can now safely return to the dashboard',
                  )
            }
          />

          <BrokerCardContainer />
          {!isSocialLoginUrl && (
            <StyledBackButton onClick={() => history.push('/dashboard')}>
              {t('Go to dashboard')}
            </StyledBackButton>
          )}
        </Flex>
      </Flex>
    </>
  );
};

const StyledAssistantQuestion = styled(AssistantQuestion)`
  margin: 0;
  align-self: center;
  margin-bottom: 1.6rem;

  h2 {
    margin: 0;
  }

  & .AssistantQuestion_avatar {
    display: none;
  }
`;

const StyledBackButton = styled(BackButton)`
  font-size: 2rem;
  padding: 0;
  margin-top: 0;
  margin-bottom: 3rem;

  svg {
    margin-right: 0.8rem;
    width: 2.4rem;
    height: 2.4rem;
  }
`;

export default CampaignsFinalizationView;
